import { InputText } from 'primereact/inputtext'
import React, { useCallback, useContext, useRef, useState } from 'react'
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox'
import { CommonContext } from '../common/context/commonContext'
import { IncomingKeepData, IncomingSearch } from '../../types/inbound'
import { getContentSize } from '../../utils/screenUtils'
import { useNavigate } from 'react-router-dom'
import { includeComma } from '../../utils/inputText'
import { useMountEffect } from 'primereact/hooks'
import { size } from 'lodash'
import { useTranslation } from 'react-i18next'
import { tokens } from 'locales/tokens'
import {translateInboundProgCd, translatePurchaseOrderInboundStatus} from '../../utils/translateCodeUtils';

const IncomingKeepGoods = () => {
    const navigate = useNavigate()
    const { $toast, $http } = useContext(CommonContext)
    const { t } = useTranslation()
    const scrollRef = useRef<HTMLDivElement>(null)
    const [total, setTotal] = useState<number>(0)
    const totalRef = useRef<number>(0)
    const [isComplete, setIsComplete] = useState<boolean>(false)
    const [searchText, setSearchText] = useState<string>('')
    const [incomings, setIncomings] = useState<IncomingKeepData[]>([])
    const scanRef = useRef<HTMLInputElement | null>(null)
    const incomingsRef = useRef<IncomingKeepData[]>([])
    const paramsRef = useRef<IncomingSearch>({
        searchText: '',
        isComplete: 'N',
        page: 1,
        pageSize: 10,
        sort: '',
        sortOrder: undefined,
        sortField: '',
    })

    const findIncoming = useCallback(
        async (params: IncomingSearch) => {
            const { total, list } = await $http().get('/inbound/incoming-keep', {
                params,
            })

            if (params.page === 1) {
                setTotal(total)
                totalRef.current = total
                setIncomings(list)
                incomingsRef.current = list
            } else {
                setIncomings(list)
                incomingsRef.current = [...incomingsRef.current, ...list]
            }

            setSearchText('')
            if (scanRef && scanRef.current) {
                scanRef.current?.focus()
            }

            if (size(list) === 0) {
                $toast({
                    title: t(tokens.inbound.check_the_expected_receipt),
                    text: t(tokens.inbound.no_information_on_the_expected_receipt_inquiry),
                    icon: 'success',
                })
                paramsRef.current = {
                    ...paramsRef.current,
                    searchText: '',
                }
            }
        },
        [$http],
    )

    const onChangesIsComplete = (e: CheckboxChangeEvent) => {
        const checked = e.checked || false
        setIsComplete(checked)

        paramsRef.current = {
            ...paramsRef.current,
            page: 1,
            isComplete: checked ? 'Y' : 'N',
        }

        findIncoming(paramsRef.current)
    }

    useMountEffect(() => {
        findIncoming(paramsRef.current)

        if (scanRef && scanRef.current) {
            scanRef.current?.focus()
        }
    })
    // useEffect(() => {
    //     findIncoming(paramsRef.current)
    // }, [paramsRef.current, findIncoming])

    const onKeyDownSearch = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            paramsRef.current = {
                ...paramsRef.current,
                searchText,
                page: 1,
            }

            findIncoming(paramsRef.current)
        }
    }

    const getCardStyle = (cd: string) => {
        switch (cd) {
            case 'PI': // 부분입고
                return {
                    background: '#ffe5e5',
                    border: '1px solid #D3D3D3',
                }
            case 'IW': // 입고대기
                return {
                    background: '#F8F9FA',
                    border: '1px solid #D3D3D3',
                }
            case 'IC': // 입고완료
                return {
                    background: '#90EE90',
                    border: '1px solid #D3D3D3',
                }
            case 'UC': // 입하완료
                return {
                    background: '#ffff8b',
                    border: '1px solid #D3D3D3',
                }
        }
    }

    const onClickDetail = (inbId: number) => {
        navigate(`/incoming-detail`, { state: { inbId: inbId } })
    }

    const findProgStyle = (cd: string) => {
        switch (cd) {
            case 'PI': // 부분입고
            case 'IW': // 입고대기
                return {
                    color: 'red',
                }
            case 'IC': // 입고완료
                return {
                    color: '#2E8B57',
                }
            case 'UC': // 입하완료
                return {
                    color: '#4169E1',
                }
        }
    }

    const pagingSearch = async () => {
        if (scrollRef.current) {
            if (paramsRef.current.page && paramsRef.current.pageSize) {
                const isNext = totalRef.current > paramsRef.current.page * paramsRef.current.pageSize
                // && scroll.offsetHeight + scroll.scrollTop >= scroll.scrollHeight - 0.5
                if (isNext) {
                    paramsRef.current = {
                        ...paramsRef.current,
                        page: paramsRef.current.page + 1,
                    }

                    const { list } = await $http().get('/inbound/incoming-keep', {
                        params: { ...paramsRef.current },
                    })

                    incomingsRef.current = [...incomingsRef.current, ...list]
                    setIncomings(incomingsRef.current)
                }
            }
        }
    }

    useMountEffect(() => {
        if (scrollRef.current) {
            const scroll = scrollRef.current
            scroll.addEventListener('scrollend', pagingSearch)
        }
    })

    return (
        <>
            <div className="grid pda-search">
                <div className="col-12 flex p-1">
                    <InputText
                        ref={scanRef}
                        value={searchText}
                        className="p-inputtext-md w-full"
                        placeholder={t(tokens.common.please_enter_the_product_name_or_barcode)}
                        onChange={(e) => setSearchText(e.currentTarget.value)}
                        onKeyDown={(e) => onKeyDownSearch(e)}
                        autoFocus={true}
                    />
                </div>
                <div className="col-12 flex pt-1">
                    <span className="font-bold text-lg rdms-text-deepblue">
                        {t(tokens.inbound.total_expected_receipt_count, { total: includeComma(total, '-') })}
                    </span>
                    <div className="ml-auto align-self-center">
                        <Checkbox checked={isComplete} onChange={onChangesIsComplete} />
                        <span className="text-lg ml-1">
                            {t(tokens.common.total)} {t(tokens.inbound.view_expected_receipt)}
                        </span>
                    </div>
                </div>
            </div>
            <div className="grid mt-1 pl-1">
                <div ref={scrollRef} className="overflow-y-scroll w-full" style={{ height: getContentSize() }}>
                    {incomings &&
                        incomings.map((incoming) => (
                            <div className="mt-0" key={`${incoming.inbId}${incoming.goodsId}`} onClick={() => onClickDetail(incoming.inbId)}>
                                <div className="card w-full p-0 pb-1 mb-1" style={getCardStyle(incoming.inbProgCd)}>
                                    <div className="col-12 justify-content-between flex">
                                        <div className="text-lg font-bold pl-1 p-0">{incoming.goodsNm} | {incoming.sysCd}</div>{' '}
                                    </div>
                                    <div className="col-12 justify-content-between flex pt-0">
                                        <div className="col-9 pl-1 p-0">{incoming.custInbInvoiceNo} | {incoming.inbExpectedLotNo} | {incoming.specTxt}</div>
                                        <div className="col-3 pr-1 p-0 text-right">{incoming.inbYmdFm}</div>
                                    </div>
                                    <div className="col-12 justify-content-between flex pt-0">
                                        <div className="font-bold" style={findProgStyle(incoming.inbProgCd)}>
                                            {incoming.inbProgCd === 'IW' && incoming.poInbSttCd === 'MI'? (
                                                <span>
                                                    {translatePurchaseOrderInboundStatus(incoming.poInbSttCd, t)}
                                                </span>
                                            ) : (
                                                <span>
                                                    {translateInboundProgCd(incoming.inbProgCd, t)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        </>
    )
}

export default IncomingKeepGoods
